import { createElement, Children, cloneElement, Fragment, Component } from 'react';
import { DraggableCore } from 'react-draggable';
import classNames from 'classnames';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

var noop = function () { };
// 去重
function unique(array, compare) {
    if (compare === void 0) { compare = function (a, b) { return a === b; }; }
    var result = [];
    var _loop_1 = function (i, len) {
        var current = array[i];
        if (result.findIndex(function (v) { return compare(v, current); }) === -1) {
            result.push(current);
        }
    };
    for (var i = 0, len = array.length; i < len; i++) {
        _loop_1(i);
    }
    return result;
}
var getMaxDistance = function (arr) {
    var num = arr.sort(function (a, b) { return a - b; });
    return num[num.length - 1] - num[0];
};
var createCoreData = function (_a, _b) {
    var node = _a.node, deltaX = _a.deltaX, deltaY = _a.deltaY;
    var originX = _b.originX, originY = _b.originY, x = _b.x, y = _b.y;
    var draglineData = {
        node: node,
        deltaY: deltaY,
        deltaX: deltaX,
        originX: originX || x,
        originY: originY || y,
        x: x,
        y: y,
    };
    return draglineData;
};

var DraggableContainer = /** @class */ (function (_super) {
    __extends(DraggableContainer, _super);
    function DraggableContainer() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        // container HTMLElement
        _this.$ = null;
        // children HTMLElement
        _this.$children = [];
        _this.state = {
            indices: [],
            vLines: [],
            hLines: [],
        };
        _this.initialize = function () {
            if (Array.isArray(_this.props.children)) {
                _this.$children = _this.props.children.map(function (_, i) {
                    // const $ = this.$.childNodes[i]
                    var $ = _this.$.children[i];
                    var x = Number($.getAttribute('data-x'));
                    var y = Number($.getAttribute('data-y'));
                    var w = $.clientWidth;
                    var h = $.clientHeight;
                    var draggableElement = {
                        $: $,
                        i: i,
                        x: x,
                        y: y,
                        w: w,
                        h: h,
                        l: x,
                        r: x + w,
                        t: y,
                        b: y + h,
                        lr: x + w / 2,
                        tb: y + h / 2,
                    };
                    return draggableElement;
                });
            }
        };
        _this.reset = function () {
            _this.setState({ vLines: [], hLines: [], indices: [] });
        };
        // 拖动中计算是否吸附/显示辅助线
        _this.calc = function (index) {
            return function (x, y) {
                var target = _this.$children[index];
                var compares = _this.$children.filter(function (_, i) { return i !== index; });
                if (_this.props.limit) {
                    var _a = _this.checkDragOut({ x: x, y: y }, target), limitX = _a.limitX, limitY = _a.limitY;
                    x = limitX;
                    y = limitY;
                }
                if (compares.length === 0) {
                    return { x: x, y: y };
                }
                return _this.calcAndDrawLines({ x: x, y: y }, target, compares);
            };
        };
        return _this;
    }
    /**
    * @param {Object} values xy坐标
    * @param {Object} target 拖拽目标
    * @param {Array} compares 对照组
    */
    DraggableContainer.prototype.calcAndDrawLines = function (values, target, compares) {
        var _this = this;
        var _a = this.calcPosValues(values, target, compares, 'x'), x = _a.v, indices_x = _a.indices, vLines = _a.lines;
        var _b = this.calcPosValues(values, target, compares, 'y'), y = _b.v, indices_y = _b.indices, hLines = _b.lines;
        var indices = unique(indices_x.concat(indices_y));
        // https://github.com/zcued/react-dragline/issues/9
        if (vLines.length && hLines.length) {
            vLines.forEach(function (line) {
                var compare = compares.find(function (_a) {
                    var i = _a.i;
                    return i === line.i;
                });
                var _a = _this.calcLineValues({ x: x, y: y }, target, compare, 'x'), length = _a.length, origin = _a.origin;
                line.length = length;
                line.origin = origin;
            });
            hLines.forEach(function (line) {
                var compare = compares.find(function (_a) {
                    var i = _a.i;
                    return i === line.i;
                });
                var _a = _this.calcLineValues({ x: x, y: y }, target, compare, 'y'), length = _a.length, origin = _a.origin;
                line.length = length;
                line.origin = origin;
            });
        }
        this.setState({
            vLines: vLines,
            hLines: hLines,
            indices: indices,
        });
        return { x: x, y: y };
    };
    DraggableContainer.prototype.calcLineValues = function (values, target, compare, key) {
        var x = values.x, y = values.y;
        var H = target.h, W = target.w;
        var l = compare.l, r = compare.r, t = compare.t, b = compare.b;
        var T = y, B = y + H, L = x, R = x + W;
        var direValues = {
            x: [t, b, T, B],
            y: [l, r, L, R],
        };
        var length = getMaxDistance(direValues[key]);
        var origin = Math.min.apply(Math, direValues[key]);
        return { length: length, origin: origin };
    };
    DraggableContainer.prototype.calcPosValues = function (values, target, compares, key) {
        var _this = this;
        var results = {};
        var directions = {
            x: ['ll', 'rr', 'lr'],
            y: ['tt', 'bb', 'tb'],
        };
        // filter unnecessary directions
        var current = directions[key];
        var validDirections = current.filter(function (dire) { return _this.props.directions.includes(dire); });
        compares.forEach(function (compare) {
            validDirections.forEach(function (dire) {
                var _a = _this.calcPosValuesSingle(values, dire, target, compare, key), near = _a.near, dist = _a.dist, value = _a.value, origin = _a.origin, length = _a.length;
                if (near) {
                    var alignToken = { i: compare.i, $: compare.$, value: value, origin: origin, length: length };
                    if (Array.isArray(results[dist])) {
                        results[dist].push(alignToken);
                    }
                    else {
                        results[dist] = [alignToken];
                    }
                }
            });
        });
        var resultArray = Object.entries(results);
        if (resultArray.length) {
            var _a = resultArray.sort(function (_a, _b) {
                var d1 = _a[0];
                var d2 = _b[0];
                return Math.abs(parseInt(d1)) - Math.abs(parseInt(d2));
            })[0], minDistance = _a[0], activeCompares = _a[1];
            var dist = parseInt(minDistance);
            return {
                v: values[key] - dist,
                dist: dist,
                lines: activeCompares,
                indices: activeCompares.map(function (_a) {
                    var i = _a.i;
                    return i;
                }),
            };
        }
        return {
            v: values[key],
            dist: 0,
            lines: [],
            indices: [],
        };
    };
    DraggableContainer.prototype.calcPosValuesSingle = function (values, dire, target, compare, key) {
        var x = values.x, y = values.y;
        var W = target.w;
        var H = target.h;
        var l = compare.l, r = compare.r, t = compare.t, b = compare.b, lr = compare.lr, tb = compare.tb;
        var _a = this.calcLineValues({ x: x, y: y }, target, compare, key), origin = _a.origin, length = _a.length;
        var result = {
            // 距离是否达到吸附阈值
            near: false,
            // 距离差
            dist: Number.MAX_SAFE_INTEGER,
            // 辅助线坐标
            value: 0,
            // 辅助线长度
            length: length,
            // 辅助线起始坐标（对应绝对定位的top/left）
            origin: origin,
        };
        switch (dire) {
            case 'lr':
                result.dist = x + W / 2 - lr;
                result.value = lr;
                break;
            case 'll':
                result.dist = x - l;
                result.value = l;
                break;
            case 'rr':
                result.dist = x + W - r;
                result.value = r;
                break;
            case 'tt':
                result.dist = y - t;
                result.value = t;
                break;
            case 'bb':
                result.dist = y + H - b;
                result.value = b;
                break;
            case 'tb':
                result.dist = y + H / 2 - tb;
                result.value = tb;
                break;
        }
        if (Math.abs(result.dist) < this.props.threshold + 1) {
            result.near = true;
        }
        return result;
    };
    // 检查是否拖出容器
    DraggableContainer.prototype.checkDragOut = function (_a, target) {
        var x = _a.x, y = _a.y;
        var maxLeft = this.$.clientWidth - target.w;
        var maxTop = this.$.clientHeight - target.h;
        var limitX = x;
        var limitY = y;
        if (x < 0) {
            limitX = 0;
        }
        else if (x > maxLeft) {
            limitX = maxLeft;
        }
        if (y < 0) {
            limitY = 0;
        }
        if (y > maxTop) {
            limitY = maxTop;
        }
        return { limitX: limitX, limitY: limitY };
    };
    DraggableContainer.prototype._renderGuideLine = function () {
        var _a = this.state, vLines = _a.vLines, hLines = _a.hLines;
        var lineStyle = this.props.lineStyle;
        var commonStyle = __assign({ position: 'absolute', backgroundColor: '#FF00CC' }, lineStyle);
        // support react 15
        var Fragment$1 = Fragment || 'div';
        return (createElement(Fragment$1, null,
            vLines.map(function (_a, i) {
                var length = _a.length, value = _a.value, origin = _a.origin;
                return (createElement("span", { className: "v-line", key: "v-" + i, style: __assign({ left: value, top: origin, height: length, width: 1 }, commonStyle) }));
            }),
            hLines.map(function (_a, i) {
                var length = _a.length, value = _a.value, origin = _a.origin;
                return (createElement("span", { className: "h-line", key: "h-" + i, style: __assign({ top: value, left: origin, width: length, height: 1 }, commonStyle) }));
            })));
    };
    DraggableContainer.prototype._renderChildren = function () {
        var _this = this;
        var _a = this.props, activeClassName = _a.activeClassName, children = _a.children;
        var indices = this.state.indices;
        return Children.map(children, function (child, index) {
            return cloneElement(child, {
                _start: _this.initialize,
                _drag: _this.calc(index),
                _stop: _this.reset,
                active: indices.includes(index),
                activeClassName: activeClassName,
            });
        });
    };
    DraggableContainer.prototype.render = function () {
        var _this = this;
        var _a = this.props, tag = _a.tag, style = _a.style;
        var Wrapper = tag;
        return (createElement(Wrapper, { style: style, ref: function (ref) { return _this.$ = ref; } },
            this._renderChildren(),
            this._renderGuideLine()));
    };
    DraggableContainer.defaultProps = {
        tag: 'div',
        style: {},
        directions: ['tt', 'bb', 'll', 'rr', 'tb', 'lr'],
        threshold: 5,
        className: '',
        activeClassName: 'active',
        limit: true,
        lineStyle: {},
    };
    return DraggableContainer;
}(Component));

var DraggableChild = /** @class */ (function (_super) {
    __extends(DraggableChild, _super);
    function DraggableChild(props) {
        var _this = _super.call(this, props) || this;
        _this.lastX = 0;
        _this.lastY = 0;
        _this.handleStart = function (ev, b) {
            var _a = _this.state, x = _a.x, y = _a.y;
            _this.lastX = b.lastX - x;
            _this.lastY = b.lastY - y;
            _this.props._start();
            _this.props.onStart(ev, createCoreData(b, { x: x, y: y }));
        };
        _this.handleDrag = function (ev, b) {
            var dragX = b.lastX - _this.lastX;
            var dragY = b.lastY - _this.lastY;
            var _a = _this.props._drag(dragX, dragY), x = _a.x, y = _a.y;
            _this.setState({ x: x, y: y });
            _this.props.onDrag(ev, createCoreData(b, {
                originX: dragX,
                originY: dragY,
                x: x,
                y: y,
            }));
        };
        _this.handleStop = function (ev, b) {
            var _a = _this.state, x = _a.x, y = _a.y;
            _this.props._stop();
            _this.props.onStop(ev, createCoreData(b, { x: x, y: y }));
        };
        _this.state = {
            x: props.defaultPosition.x,
            y: props.defaultPosition.y,
        };
        return _this;
    }
    DraggableChild.prototype.render = function () {
        var _a, _b;
        var _c = this.state, x = _c.x, y = _c.y;
        var _d = this.props, active = _d.active, children = _d.children, activeClassName = _d.activeClassName;
        var style = __assign(__assign({}, children.props.style), { position: 'absolute', top: y, left: x });
        var className = classNames(children.props.className, (_a = {},
            _a[activeClassName] = active,
            _a));
        return (createElement(DraggableCore, { grid: [1, 1], onDrag: this.handleDrag, onStop: this.handleStop, onStart: this.handleStart }, cloneElement(this.props.children, (_b = {
                style: style,
                className: className
            },
            _b['data-x'] = x,
            _b['data-y'] = y,
            _b))));
    };
    DraggableChild.defaultProps = {
        defaultPosition: { x: 0, y: 0 },
        onStart: noop,
        onDrag: noop,
        onStop: noop,
    };
    return DraggableChild;
}(Component));

export { DraggableChild, DraggableContainer };
